/**
 * Catalog Service
 * Handles catalog-related operations including Priority sync and catalog loading
 */
import { callFunction } from '../firebase/functions.js'
import { getDocument } from '../firebase/firestore.js'
import { getFirebaseDb } from '../firebase/firebase.js'
import { collection, doc, getDoc, getDocs, query, where, documentId } from 'firebase/firestore'
import logger from './logger.js'

const log = logger.createScoped('CatalogService')

// ============================================
// Constants
// ============================================
const DEFAULT_ASSORTMENT_ID = '10001'
const DEFAULT_PRICE_LIST_ID = 'בסיס'
const FIRESTORE_IN_QUERY_LIMIT = 30 // Firestore 'in' query limit (max 30)

/**
 * Pull catalog items from Priority ERP and sync to Firestore
 * Calls the priorityPullCatalogItems callable function
 * @param {Object} options
 * @param {boolean} [options.dryRun=false] - If true, skip Firestore writes (if supported by backend)
 * @returns {Promise<Object>} Result object
 * @returns {boolean} result.ok - Whether the sync completed
 * @returns {number} result.fetchedCount - Total items fetched from Priority
 * @returns {number} result.writtenCount - Items written to Firestore
 * @returns {number} result.skippedCount - Items filtered by validation (not errors)
 * @returns {number} result.newCount - Items that didn't exist before in Firestore
 * @returns {number} result.failedCount - Actual errors (mapping exceptions + batch write failures)
 * @returns {Array<{makat: string, name: string}>} result.newItems - New items list for display
 * @returns {string} result.collection - Firestore collection name
 */
export async function pullCatalogItemsFromPriority(options = {}) {
  const { dryRun = false } = options

  log.info('Starting Priority catalog pull', { dryRun })

  try {
    const result = await callFunction('priorityPullCatalogItems', { dryRun })

    log.info('Priority catalog pull completed', {
      fetchedCount: result.fetchedCount,
      writtenCount: result.writtenCount,
      skippedCount: result.skippedCount,
      newCount: result.newCount,
      failedCount: result.failedCount,
      collection: result.collection,
    })

    return result
  } catch (error) {
    log.error('Priority catalog pull failed', error)
    throw error
  }
}

/**
 * Pull catalog images from Priority ERP (single batch)
 * Fetches images for items missing the 'image' field
 * @param {Object} options
 * @param {number} [options.batchSize=50] - Number of items to process per batch (10-200)
 * @param {string|null} [options.startAfter=null] - Cursor (makat) to start after for pagination
 * @returns {Promise<Object>} Result with processed, success, failed, skipped, nextCursor, done
 */
export async function pullCatalogImagesFromPriority(options = {}) {
  const { batchSize = 50, startAfter = null } = options

  log.info('Starting Priority images pull batch', { batchSize, startAfter })

  try {
    const result = await callFunction('priorityPullCatalogImages', {
      batchSize,
      startAfter,
    })

    log.info('Priority images batch completed', {
      processed: result.processed,
      success: result.success,
      failed: result.failed,
      skipped: result.skipped,
      done: result.done,
    })

    return result
  } catch (error) {
    log.error('Priority images pull failed', error)
    throw error
  }
}

/**
 * Refresh a single catalog item's image from Priority ERP.
 * Overwrites the Storage file and Firestore image field unconditionally.
 * @param {string} makat
 * @returns {Promise<{ ok: boolean, makat: string, imageUrl: string, updatedAt: string }>}
 */
export async function refreshCatalogImage(makat) {
  log.info('Refreshing catalog image', { makat })

  try {
    const result = await callFunction('priorityRefreshCatalogImage', { makat })
    log.info('Catalog image refresh completed', { makat, imageUrl: result.imageUrl })
    return result
  } catch (error) {
    log.error('Catalog image refresh failed', { makat, error })
    throw error
  }
}

/**
 * Replace a catalog item's image with a processed PNG (e.g. after background removal).
 * Uploads to Storage as Catalog/{makat}.png, updates the Firestore image field with a
 * cache-busted URL, and removes the previous file when its extension changed.
 * @param {string} makat
 * @param {string} imageBase64 - PNG as a base64 data URL
 * @returns {Promise<{ ok: boolean, makat: string, imageUrl: string, updatedAt: string }>}
 */
export async function replaceCatalogImage(makat, imageBase64) {
  log.info('Replacing catalog image', { makat })

  try {
    const result = await callFunction('catalogReplaceImage', { makat, imageBase64 })
    log.info('Catalog image replace completed', { makat, imageUrl: result.imageUrl })
    return result
  } catch (error) {
    log.error('Catalog image replace failed', { makat, error })
    throw error
  }
}

// ============================================
// Catalog Loader Functions
// ============================================

/**
 * Split array into chunks of specified size
 * @param {Array} array
 * @param {number} size
 * @returns {Array<Array>}
 */
function chunkArray(array, size) {
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

/**
 * Fetch catalog items by makats in chunks (parallel execution)
 * Uses documentId() 'in' queries with chunking to avoid Firestore limits
 * @param {Array<string>} makats - Array of makat IDs to fetch
 * @returns {Promise<Map<string, Object>>} Map of makat -> catalog item
 */
async function fetchCatalogItemsByMakats(makats) {
  if (!makats || makats.length === 0) {
    return new Map()
  }

  const db = getFirebaseDb()
  const catalogRef = collection(db, 'Catalog')
  const chunks = chunkArray(makats, FIRESTORE_IN_QUERY_LIMIT)
  const itemsMap = new Map()

  log.info(`Fetching ${makats.length} catalog items in ${chunks.length} parallel chunks`)

  // Execute all chunk queries in parallel
  const chunkPromises = chunks.map((chunk) => {
    const q = query(catalogRef, where(documentId(), 'in', chunk))
    return getDocs(q)
  })

  const snapshots = await Promise.all(chunkPromises)

  // Merge results from all snapshots
  for (const snapshot of snapshots) {
    snapshot.forEach((docSnap) => {
      itemsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() })
    })
  }

  log.info(`Fetched ${itemsMap.size} catalog items`)
  return itemsMap
}

/**
 * Load catalog for user based on their profile
 * @deprecated Use the catalog store (useCatalogStore) instead.
 * The store handles both default and client-linked catalog modes with proper caching.
 * This function is kept for backwards compatibility but delegates to the store.
 * @param {Object} userProfile - User profile from auth store
 * @returns {Promise<Object>} Result with mode, assortmentId, priceListId, items
 */
export async function loadCatalogForUser(userProfile) {
  log.warn('loadCatalogForUser is deprecated. Use the catalog store (useCatalogStore) instead.')

  // For backwards compatibility, load default catalog only
  // For full client-linked support, use the catalog store
  const assortment = await getDocument('Assortments', DEFAULT_ASSORTMENT_ID)

  if (!assortment) {
    throw new Error(`Assortment ${DEFAULT_ASSORTMENT_ID} not found`)
  }

  const assortmentMakats = assortment.itemMakats || []
  if (assortmentMakats.length === 0) {
    return {
      mode: 'default',
      assortmentId: DEFAULT_ASSORTMENT_ID,
      priceListId: DEFAULT_PRICE_LIST_ID,
      items: [],
    }
  }

  const priceList = await getDocument('Prices', DEFAULT_PRICE_LIST_ID)
  if (!priceList) {
    throw new Error(`Price list ${DEFAULT_PRICE_LIST_ID} not found`)
  }

  const priceMap = new Map()
  if (priceList.items && Array.isArray(priceList.items)) {
    for (const item of priceList.items) {
      if (item.makat && item.price !== undefined) {
        priceMap.set(item.makat, item.price)
      }
    }
  }

  const catalogItemsMap = await fetchCatalogItemsByMakats(assortmentMakats)

  const items = []
  for (const makat of assortmentMakats) {
    const catalogItem = catalogItemsMap.get(makat)
    if (catalogItem) {
      items.push({
        ...catalogItem,
        price: priceMap.get(makat) ?? null,
      })
    }
  }

  return {
    mode: 'default',
    assortmentId: DEFAULT_ASSORTMENT_ID,
    priceListId: DEFAULT_PRICE_LIST_ID,
    items,
  }
}
